if (!customElements.get('product-information-rail')) {
  class ProductInformationRail extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-product-detail-viewport]');
      this.panels = [...this.querySelectorAll('[data-product-detail-panel]')];
      if (!this.viewport || !this.panels.length) return;
      this.adaptiveHeight = Boolean(this.querySelector('.edc-mode-runtimes'));
      if (this.adaptiveHeight) this.viewport.setAttribute('data-adaptive-height', '');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.playback = this.querySelector('[data-product-detail-playback]');
      this.position = this.querySelector('[data-product-detail-position]');
      this.nav = this.querySelector('[data-product-detail-nav]');
      this.paused = false;
      this.index = 0;
      this.nav.replaceChildren();
      this.tabs = this.panels.map((panel, index) => {
        panel.setAttribute('role', 'group');
        panel.setAttribute('aria-roledescription', 'slide');
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = panel.querySelector('h3').textContent;
        button.setAttribute('aria-controls', panel.id);
        button.addEventListener('click', () => this.goTo(index), options);
        this.nav.append(button);
        return button;
      });
      this.querySelector('[data-product-detail-previous]').addEventListener('click', () => this.goTo(this.index - 1), options);
      this.querySelector('[data-product-detail-next]').addEventListener('click', () => this.goTo(this.index + 1), options);
      this.playback.addEventListener('click', () => {
        this.paused = !this.paused;
        this.playback.textContent = this.paused ? 'Play' : 'Pause';
        this.playback.setAttribute('aria-label', this.paused ? 'Resume automatic scrolling' : 'Pause automatic scrolling');
        this.position.setAttribute('aria-live', this.paused ? 'polite' : 'off');
        this.schedule();
      }, options);
      this.viewport.addEventListener('keydown', (event) => {
        if (event.target !== this.viewport || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.viewport.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.viewportWidth = this.viewport.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.adaptiveHeight || this.viewport.clientWidth !== this.viewportWidth) {
          this.viewportWidth = this.viewport.clientWidth;
          this.viewport.scrollTo({ left: this.panels[this.index].offsetLeft, behavior: 'instant' });
        }
        this.update();
      });
      this.resizeObserver.observe(this.viewport);
      if (this.adaptiveHeight) {
        this.panels.forEach((panel) => this.resizeObserver.observe(panel.querySelector('.edc-product-rail__layout')));
        this.addEventListener('load', () => this.updateHeight(), { ...options, capture: true });
        document.fonts?.ready.then(() => { if (this.isConnected) this.updateHeight(); });
        document.fonts?.addEventListener('loadingdone', () => this.updateHeight(), options);
      }
      this.update();
      this.schedule();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.scrollFrame);
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.paused || this.panels.length < 2) return;
      this.timer = setTimeout(() => {
        if (document.hidden) this.schedule();
        else this.goTo(this.index + 1);
      }, 12000);
    }

    goTo(index) {
      const next = (index + this.panels.length) % this.panels.length;
      const wrapping = index < 0 || index >= this.panels.length;
      if (this.panels[this.index].contains(document.activeElement)) this.playback.focus({ preventScroll: true });
      this.viewport.scrollTo({
        left: this.panels[next].offsetLeft,
        behavior: wrapping || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
      this.schedule();
    }

    updateHeight() {
      if (!this.adaptiveHeight) return;
      // Keep ordinary slides stable; only the selected longer slide can expand the rail.
      const heights = this.panels.map((panel) => panel.querySelector('.edc-product-rail__layout').getBoundingClientRect().height);
      const normalHeight = Math.max(0, ...heights.filter((height, index) => !this.panels[index].querySelector('.edc-mode-runtimes')));
      const height = Math.ceil(Math.max(normalHeight, heights[this.index]));
      if (this.viewport.style.height !== `${height}px`) this.viewport.style.height = `${height}px`;
    }

    update() {
      // A resize can dispatch scroll before ResizeObserver; preserve the selected slide.
      if (this.adaptiveHeight && this.viewport.clientWidth !== this.viewportWidth) {
        this.viewportWidth = this.viewport.clientWidth;
        this.viewport.scrollTo({ left: this.panels[this.index].offsetLeft, behavior: 'instant' });
      }
      const previousIndex = this.index;
      this.index = this.panels.reduce((best, panel, index) =>
        Math.abs(panel.offsetLeft - this.viewport.scrollLeft) < Math.abs(this.panels[best].offsetLeft - this.viewport.scrollLeft) ? index : best, 0);
      this.panels.forEach((panel, index) => {
        const active = index === this.index;
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
        this.tabs[index].setAttribute('aria-current', String(active));
      });
      this.position.textContent = `${this.index + 1} / ${this.panels.length}`;
      this.updateHeight();
      if (previousIndex !== this.index) {
        const tab = this.tabs[this.index];
        if (tab.offsetLeft < this.nav.scrollLeft || tab.offsetLeft + tab.offsetWidth > this.nav.scrollLeft + this.nav.clientWidth) {
          this.nav.scrollTo({ left: tab.offsetLeft - this.nav.offsetLeft, behavior: 'instant' });
        }
      }
    }
  }
  customElements.define('product-information-rail', ProductInformationRail);
}
